import { StickyNote, Trash2, Check, CheckCheck } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { commsAPI, errMsg, fmtFull } from '../../lib/communicationsAPI';
import { RichBody } from './RichBody';
import { AttachmentList } from './Attachments';
import { EmailCard } from './EmailBody';

const Bubble = ({ m }) => {
  if (m.channel === 'email') return <EmailCard m={m} />;
  const out = m.direction === 'outbound';
  return (
    <div className={cn('flex', out ? 'justify-end' : 'justify-start')} data-testid={`comms-message-${m.id}`}>
      <div className={cn('max-w-[78%] rounded-2xl px-4 py-2.5 shadow-sm',
        out ? 'bg-primary text-primary-foreground rounded-br-md' : 'bg-muted text-foreground rounded-bl-md')}>
        <p className={cn('text-[11px] font-semibold mb-0.5', out ? 'text-primary-foreground/80' : 'text-foreground/70')}>
          {m.sender_name || (out ? 'Firm' : 'Client')}
        </p>
        <RichBody body={m.body} format={m.body_format} className={out ? 'comms-prose-invert' : ''} testId={`comms-message-body-${m.id}`} />
        <AttachmentList items={m.attachments} staff inverted={out} />
        <p className={cn('mt-1 text-[10px] flex items-center gap-1', out ? 'text-primary-foreground/70 justify-end' : 'text-muted-foreground')}>
          {fmtFull(m.sent_at)}
          {out && (m.read_at ? <CheckCheck className="w-3 h-3" aria-label="Read by client" /> : <Check className="w-3 h-3" aria-label="Delivered" />)}
        </p>
      </div>
    </div>
  );
};

const Note = ({ n, onDeleted }) => {
  const del = async () => {
    try { await commsAPI.deleteNote(n.id); toast.success('Note deleted'); onDeleted(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="mx-auto w-full max-w-[90%] rounded-lg border border-amber-300/70 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700/50 px-4 py-2.5" data-testid={`comms-note-${n.id}`}>
      <div className="flex items-center gap-2 text-[11px] font-semibold text-amber-800 dark:text-amber-200">
        <StickyNote className="w-3.5 h-3.5" /> Internal note · {n.author_name}
        <span className="font-normal text-amber-700/80 dark:text-amber-300/70">{fmtFull(n.created_at)}</span>
        <button type="button" onClick={del} className="ml-auto opacity-60 hover:opacity-100" aria-label="Delete note" data-testid={`comms-note-delete-${n.id}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      <p className="text-sm whitespace-pre-wrap break-words text-amber-950 dark:text-amber-50 mt-1">{n.body}</p>
      {n.mention_names?.length > 0 && (
        <p className="text-[11px] text-amber-700 dark:text-amber-300 mt-1">Mentioned: {n.mention_names.filter(Boolean).join(', ')}</p>
      )}
    </div>
  );
};

// Messages + internal notes interleaved chronologically.
export const Thread = ({ messages, notes, onChanged }) => {
  const items = [
    ...messages.map((m) => ({ kind: 'm', at: m.sent_at, m })),
    ...notes.map((n) => ({ kind: 'n', at: n.created_at, n })),
  ].sort((a, b) => (a.at < b.at ? -1 : 1));
  return (
    <div className="space-y-3" data-testid="comms-thread">
      {items.map((it) => (it.kind === 'm' ? <Bubble key={it.m.id} m={it.m} /> : <Note key={it.n.id} n={it.n} onDeleted={onChanged} />))}
    </div>
  );
};
